import { Col, Row } from 'react-bootstrap';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import {
  useGetLatestIncidentWorkLogForUser,
  WORK_LOG_ACTIONS,
  useCreateIncidentMemberSession,
  useUpdateIncidentMemberSession,
} from 'hooks/incident/useWorkLog';
import {
  useMarkIncidentUserActive,
  useMarkIncidentUserInactive,
} from 'hooks/incident/useRoster';
import { useActivationActions } from 'hooks/incident/useActivationActions';
import {
  useUserGuid,
  useGroupGuid,
  useActualIncidentId,
} from '../hooks/commonHooks';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useHwcgFlag } from 'utils/featureFlags';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  HWCG_ACTIVATION_CHOICES,
  canSubmitHwcgActivationResponse,
  type HwcgActivationResponse,
} from './hwcgActivationResponse';

const activationStatusOptions = [
  { label: 'Activated', value: 'activated' },
  { label: 'Deactivated', value: 'deactivated' },
];

const checkInStatusOptions = [
  { label: 'Checked In', value: 'checked_in' },
  { label: 'Demobilized', value: 'checked_out' },
];

const signInStatusOptions = [
  { label: 'Signed In', value: 'signed_in' },
  { label: 'Not Signed In', value: 'signed_out' },
];

const MemberStatusControls: React.FC = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useUserGuid();
  const groupGuid = useGroupGuid();
  const hwcg = useHwcgFlag();

  const { data: userWorkLog } = useGetLatestIncidentWorkLogForUser(
    incidentId,
    userGuid
  );

  const markActive = useMarkIncidentUserActive(incidentId, groupGuid);
  const markInactive = useMarkIncidentUserInactive(incidentId, groupGuid);
  const { mutateAsync: handleActivationUpdate, isPending: activationPending } =
    useActivationActions();
  const createSession = useCreateIncidentMemberSession();
  const updateSession = useUpdateIncidentMemberSession();

  const [hwcgResponse, setHwcgResponse] =
    useState<HwcgActivationResponse | null>(null);
  const [hwcgComment, setHwcgComment] = useState('');

  const currentSession = userWorkLog?.sessions.length
    ? userWorkLog.sessions[0]
    : undefined;

  const pendingHwcgActivation =
    hwcg && userWorkLog?.activationStatus === 'pending';

  const activationStatus = useMemo(
    () =>
      activationStatusOptions.find(
        (opt) => opt.value === userWorkLog?.activationStatus
      ) || activationStatusOptions[1],
    [userWorkLog?.activationStatus]
  );

  const checkInStatus = useMemo(
    () =>
      checkInStatusOptions.find(
        (opt) => opt.value === userWorkLog?.checkInStatus
      ) || checkInStatusOptions[1],
    [userWorkLog?.checkInStatus]
  );

  const signInStatus = useMemo(
    () =>
      signInStatusOptions.find(
        (opt) => opt.value === userWorkLog?.signInStatus
      ) || signInStatusOptions[1],
    [userWorkLog?.signInStatus]
  );

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleString();
  };

  const commentRequired =
    hwcgResponse !== null && hwcgResponse !== 'accepted';
  const canSubmitHwcg = canSubmitHwcgActivationResponse({
    response: hwcgResponse,
    comment: hwcgComment,
    pending: activationPending,
  });

  const submitHwcgActivation = async () => {
    if (!hwcgResponse || !canSubmitHwcg) return;
    try {
      await handleActivationUpdate({
        incidentId,
        memberUserGuid: userGuid,
        isActivated: hwcgResponse === 'accepted',
        activationResponse: hwcgResponse,
        responseComment:
          hwcgResponse === 'accepted' ? undefined : hwcgComment.trim(),
      });
      setHwcgComment('');
      setHwcgResponse(null);
    } catch {
      // Toast handled by mutation.
    }
  };

  const handleActivationStatusChange = (value: unknown) => {
    const selectedValue = (value as OptionType).value;
    handleActivationUpdate({
      incidentId: incidentId,
      memberUserGuid: userGuid,
      isActivated: selectedValue === 'activated',
    });
  };

  const handleCheckInStatusChange = (value: unknown) => {
    const selectedValue = (value as OptionType)?.value;
    if (selectedValue === 'checked_in' && userWorkLog?.incidentMemberId) {
      markActive.mutate({
        user_guid: userGuid,
        incident_member_id: userWorkLog.incidentMemberId,
      });
    } else if (
      selectedValue === 'checked_out' &&
      userWorkLog?.loginTime &&
      userWorkLog?.incidentMemberId
    ) {
      // Prevent checking out when signed in
      if (currentSession?.signInTime && !currentSession?.signOutTime) {
        toast.error('You must sign out before checking out');
        return;
      }
      const loginTime = new Date(userWorkLog.loginTime);
      const logoutTime = new Date();
      const minutesWorked = Math.floor(
        (logoutTime.getTime() - loginTime.getTime()) / 60000
      );
      markInactive.mutate({
        user_guid: userGuid,
        logout_time: logoutTime,
        minutes_worked: minutesWorked,
        incident_member_id: userWorkLog.incidentMemberId,
      });
    }
  };

  const handleSignInStatusChange = (value: unknown) => {
    const selectedValue = (value as OptionType).value;
    if (selectedValue === 'signed_in' && userWorkLog?.workLogId) {
      createSession.mutate({
        incidentId: incidentId,
        userGuid,
        workLogId: userWorkLog.workLogId,
        signInTime: new Date().toISOString(),
      });
    } else if (
      selectedValue === 'signed_out' &&
      currentSession &&
      userWorkLog?.workLogId
    ) {
      updateSession.mutate({
        incidentId: incidentId,
        userGuid,
        workLogId: userWorkLog.workLogId,
        sessionId: currentSession.sessionId,
        signOutTime: new Date().toISOString(),
      });
    }
  };

  return (
    <Row>
      <Col>
        {pendingHwcgActivation ? (
          <div
            className="tw:flex tw:flex-col tw:gap-3 tw:rounded-md tw:border tw:border-border tw:p-3"
            data-testid="hwcg-activation-response"
          >
            <div>
              <div className="tw:text-sm tw:font-medium">Activation request</div>
              <p className="tw:m-0 tw:mt-1 tw:text-xs tw:text-muted-foreground">
                You have been requested to activate for this incident. Choose a
                response below.
              </p>
            </div>
            <fieldset className="tw:m-0 tw:flex tw:flex-col tw:gap-2 tw:border-0 tw:p-0">
              <legend className="tw:sr-only">Activation response</legend>
              {HWCG_ACTIVATION_CHOICES.map((choice) => (
                <label
                  key={choice.value}
                  className="tw:flex tw:cursor-pointer tw:items-start tw:gap-2 tw:text-sm"
                >
                  <input
                    type="radio"
                    name="hwcg-activation-response"
                    className="tw:mt-1"
                    checked={hwcgResponse === choice.value}
                    onChange={() => setHwcgResponse(choice.value)}
                  />
                  <span>{choice.label}</span>
                </label>
              ))}
            </fieldset>
            {commentRequired ? (
              <div className="tw:flex tw:flex-col tw:gap-1">
                <Label htmlFor="hwcg-activation-comment">
                  Comments <span className="tw:text-destructive">*</span>
                </Label>
                <Textarea
                  id="hwcg-activation-comment"
                  value={hwcgComment}
                  maxLength={1000}
                  rows={3}
                  placeholder="Explain your response for the person who requested activation"
                  onChange={(event) => setHwcgComment(event.target.value)}
                />
              </div>
            ) : null}
            <div className="tw:flex tw:justify-end">
              <Button
                type="button"
                size="sm"
                disabled={!canSubmitHwcg}
                onClick={() => {
                  void submitHwcgActivation();
                }}
              >
                {activationPending ? 'Submitting…' : 'Submit response'}
              </Button>
            </div>
            {userWorkLog?.activationResendAt ? (
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                Resend requested for{' '}
                {formatDate(userWorkLog.activationResendAt)}
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <PratusSelect
              label="Activation Status"
              value={activationStatus}
              options={activationStatusOptions}
              onChange={handleActivationStatusChange}
              isDisabled={
                !(
                  userWorkLog?.availableActions.includes(
                    WORK_LOG_ACTIONS.activate
                  ) ||
                  userWorkLog?.availableActions.includes(
                    WORK_LOG_ACTIONS.decline
                  )
                )
              }
            />
            {userWorkLog?.activatedTimestamp && (
              <div className="mt-2 ms-1">
                Activated at {formatDate(userWorkLog.activatedTimestamp)}
              </div>
            )}
          </>
        )}
      </Col>
      <Col>
        <PratusSelect
          label="Checked-In Status"
          value={checkInStatus}
          options={checkInStatusOptions}
          onChange={handleCheckInStatusChange}
          isDisabled={
            !(
              userWorkLog?.availableActions.includes(
                WORK_LOG_ACTIONS.checkIn
              ) ||
              userWorkLog?.availableActions.includes(WORK_LOG_ACTIONS.checkOut)
            )
          }
        />
        {userWorkLog?.loginTime && (
          <div className="mt-2 ms-1">
            Checked in at {formatDate(userWorkLog.loginTime)}
          </div>
        )}
      </Col>
      <Col>
        <PratusSelect
          label="Sign-In Status"
          value={signInStatus}
          options={signInStatusOptions}
          onChange={handleSignInStatusChange}
          isDisabled={
            !(
              userWorkLog?.availableActions.includes(WORK_LOG_ACTIONS.signIn) ||
              userWorkLog?.availableActions.includes(WORK_LOG_ACTIONS.signOut)
            )
          }
        />
        {currentSession?.signInTime && (
          <div className="mt-2 ms-1">
            Signed In at {formatDate(currentSession.signInTime)}
          </div>
        )}
      </Col>
    </Row>
  );
};

export default MemberStatusControls;
